import { makeStyles } from '@mui/styles';
import Loader from 'apollo-react/components/Loader';
import Modal from 'apollo-react/components/Modal';
import TextField from 'apollo-react/components/TextField';
import TreeItem from 'apollo-react/components/TreeItem';
import TreeView from 'apollo-react/components/TreeView';
import Close from 'apollo-react-icons/Close';
import SearchIcon from 'apollo-react-icons/Search';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import { INPUT_ICON_DEFAULT_SIZE } from '../../constants/CommonStylesConstants';
import { useTranslation } from '../../custom-hooks/useTranslation';
import { filterListItems } from '../../utils/searchModalUtils';

const useStyles = makeStyles({
  modal: {
    width: 650,
    height: '70%',
  },
  search: {
    marginTop: 0,
  },
  clearIcon: INPUT_ICON_DEFAULT_SIZE,
});

const renderList = ({
  defaultItemExpanded,
  listItems = [],
  showItemIcons,
  handleSelect,
  handleConfirm,
}) => {
  const renderTree = (nodes) => (
    <TreeItem key={nodes.id} nodeId={nodes.id} label={nodes.label}>
      {Array.isArray(nodes.children) ? nodes.children.map((node) => renderTree(node)) : null}
    </TreeItem>
  );

  return (
    <TreeView
      variant={showItemIcons ? 'folder' : 'default'}
      defaultExpanded={defaultItemExpanded}
      onNodeSelect={handleSelect}
      onDoubleClick={handleConfirm}
    >
      {listItems.map((item) => renderTree(item))}
    </TreeView>
  );
};

const SearchModal = ({
  open,
  modalTitle = '',
  confirmButtonLabel = 'Ok',
  fetchData,
  getTranslatedListItems,
  modifyListItems,
  onConfirm,
  closeModal,
  defaultItemExpanded,
}) => {
  const classes = useStyles();
  const translateString = useTranslation();

  const [searchText, setSearchText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState();
  const [initListItems, setInitListItems] = useState([]);

  const searchInputBox = useRef();

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      const items = await fetchData();

      setInitListItems(items);
      setIsLoading(false);

      searchInputBox?.current.focus();
    };

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredListItems = useMemo(() => {
    return filterListItems({
      listItems: initListItems,
      getTranslatedListItems,
      translateString,
      search: searchText,
    });
  }, [initListItems, searchText, translateString, getTranslatedListItems]);

  const handleSearchChange = ({ target: { value } }) => {
    const newSearchText = value.length === 1 ? value.trim() : value;

    setSearchText(newSearchText);
  };

  const clearSearchText = () => {
    setSearchText('');
    searchInputBox.current.focus();
  };

  const handleSelect = (_, nodeId) => {
    const selectedItem = filteredListItems.find((item) => item.id === nodeId);
    selectedItem && setSelectedItem(selectedItem);
  };

  const handleConfirm = () => {
    selectedItem && onConfirm(selectedItem);
  };

  return (
    <Modal
      className={classes.modal}
      open={open}
      onClose={closeModal}
      disableBackdropClick
      title={translateString(modalTitle)}
      buttonProps={[
        { label: 'Cancel', onClick: closeModal },
        {
          label: translateString(confirmButtonLabel),
          variant: 'primary',
          onClick: handleConfirm,
          disabled: !selectedItem,
        },
      ]}
    >
      <TextField
        className={classes.search}
        startIcon={<SearchIcon />}
        icon={searchText !== '' ? <Close ui-ta="clear-icon" className={classes.clearIcon} /> : null}
        iconProps={{ onClick: clearSearchText }}
        placeholder={translateString('Search')}
        name="Search"
        fullWidth
        value={searchText}
        onChange={handleSearchChange}
        inputRef={searchInputBox}
      />
      {isLoading && <Loader isInner />}
      {renderList({
        listItems: modifyListItems
          ? modifyListItems({ listItems: filteredListItems, translateString })
          : filteredListItems,
        showItemIcons: !!modifyListItems,
        defaultItemExpanded,
        handleSelect,
        handleConfirm,
      })}
    </Modal>
  );
};

export default SearchModal;
